import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnce } from 'react-use';
import { EStatementPreferences } from './components/EStatementPreferences/EStatementPreferences';
import { WorkflowComponentProps } from '../Workflow/Workflow.meta';
import { useIsFolderReadOnly } from './hooks/useIsFolderReadOnly';
import { useDocumentTitle } from '../_hooks/useDocumentTitle';
import { useDropzoneOptions } from './hooks/useDropzoneOptions';
import { useEntityList } from './components/EntityList/useEntityList';
import { DatePicker } from './components/DatePicker/DatePicker';
import { WorkflowFooter } from '../Workflow/components/WorkflowFooter/WorkflowFooter';
import { useHasAccessToDocumentVault } from './hooks/useHasAccessToDocumentVault';
import { AuthenticationAuthorityDTO, LEARN_MORE_LINK } from './DocumentVault.meta';
import { Browser } from './components/Browser/Browser';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { Link } from '../CommonComponents/OPL/Link/Link';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { resetState, setIsWorkFlow } from './state/actions';
import { hasEStatementSettingsSelector } from '../CommonSelectors/CommonAreaSelector';
import { DefaultWrapper, WorkflowWrapper } from '../Workflow/Workflow.styles';
import { DocumentVaultProvider } from './context/DocumentVaultProvider';
import { useDocumentVault } from './useDocumentVault';
import { FidelityAuthentication } from './components/FidelityAuthentication/FidelityAuthentication';
import { useDocumentVaultContext } from './context/useDocumentVaultContext';
import { useRouteParams } from './hooks/useRouteParams';
import { StickyFooter } from '../CommonComponents/OPL/StickyFooter/StickyFooter';
import { useResetModuleWrapperStyles } from '../_hooks/useResetModuleWrapperStyles';
import { useClientId } from '../_hooks/useClientId';

let helpKey;

const isAdviser = !ADV.data.cx;

interface DocumentVaultContentProps {
  workflowProps?: WorkflowComponentProps;
}

export const DocumentVault = (props: DocumentVaultContentProps) => {
  const {
    data: { selectedEntity, forceYear },
    actions: { setSelectedEntity, setForceYear },
  } = useDocumentVault();

  const hasAccessToDocumentVault = useHasAccessToDocumentVault();

  const { previousComponent } = useRouteParams();

  if (!hasAccessToDocumentVault) {
    return null;
  }

  return (
    <DocumentVaultProvider value={{ setSelectedEntity, selectedEntity, forceYear, setForceYear }}>
      <DocumentVaultContent {...props} previousComponent={previousComponent} />
    </DocumentVaultProvider>
  );
};

interface ContentProps extends DocumentVaultContentProps {
  previousComponent?: string;
}

const DocumentVaultContent = ({ workflowProps, previousComponent }: ContentProps) => {
  useResetModuleWrapperStyles({ isActive: !!previousComponent });

  const clientId = useClientId();

  const dispatch = useDispatch();

  const isWorkflow = !!workflowProps;

  const hasEStatementSettings = useSelector(hasEStatementSettingsSelector);

  const [isEStatementPreferencesModalOpen, setIsEStatementPreferencesModalOpen] = useState(false);

  const isFolderReadOnly = useIsFolderReadOnly();

  const dropzoneOptions = useDropzoneOptions();

  const { selectedEntity } = useDocumentVaultContext();

  const { isFetching, isError, data } = useEntityList();

  // This dropzone is used when the user drags files to a non-empty folder
  // We use the top most container as the root to improve UX
  const { isDragActive, getRootProps } = useDropzone({
    ...dropzoneOptions,
    noKeyboard: true,
    disabled:
      isFetching ||
      isError ||
      !data ||
      data.items.length === 0 || // Show this dropzone just for non-empty folders
      isFolderReadOnly,
  });

  useEffectOnce(() => {
    helpKey = ADV.util.setHelp(ADV.HelpDescriptors.DocumentVault);

    dispatch(setIsWorkFlow({ isWorkflow }));

    return () => {
      ADV.util.unsetHelp(helpKey);

      dispatch(resetState());
    };
  });

  useDocumentTitle('Document Vault');

  const Wrapper = isWorkflow ? WorkflowWrapper : DefaultWrapper;

  return (
    <Box css="overflow-x: auto">
      <Wrapper>
        <Box
          display="flex"
          flexDirection="column"
          minWidth={isWorkflow ? 'revert' : 1000}
          height="100%"
          mx={-30}
          px={30}
          gap={12}
          {...getRootProps()}
        >
          <Box display="flex" justifyContent="space-between">
            <Text as="h1" fontSize="h4" fontWeight={500} mb="24">
              Document Vault
            </Text>

            <Box mb="8">
              {hasEStatementSettings && (
                <Button
                  leftIcon="far fa-cog"
                  variant="secondary"
                  mr={
                    selectedEntity?.authenticationAuthority ===
                    AuthenticationAuthorityDTO.FidelityDocuments
                      ? 8
                      : 0
                  }
                  onClick={() => setIsEStatementPreferencesModalOpen(true)}
                  mb="8"
                >
                  eStatement Settings
                </Button>
              )}

              <DatePicker />
            </Box>
          </Box>

          <Browser isDragActive={isDragActive} />

          {!isAdviser && (
            <Text mt="16" color="neutral.500" fontSize="small" textAlign="center">
              All documents will be accessible by your advisor
            </Text>
          )}

          <Box display="flex" justifyContent="center" mt="24">
            <Link isExternal href={LEARN_MORE_LINK}>
              Learn about Document Vault’s security
            </Link>
          </Box>
        </Box>
      </Wrapper>

      {previousComponent && (
        <StickyFooter maxContentWidth={1200}>
          <Button
            onClick={() => {
              ADV.util.Router.go2('AdvancedPDF', {
                clientId,
              });
            }}
            variant="secondary"
            leftIcon="fas fa-chevron-left"
            size="large"
            mr="auto"
          >
            Back {previousComponent === 'AdvancedPDF' ? 'to Report Templates' : ''}
          </Button>
        </StickyFooter>
      )}

      {workflowProps && (
        <WorkflowFooter
          isFirstStep={workflowProps.isFirstStep}
          onPrevious={workflowProps.onPrevious}
          onNext={workflowProps.onNext}
        />
      )}

      {isEStatementPreferencesModalOpen && (
        <EStatementPreferences onClose={() => setIsEStatementPreferencesModalOpen(false)} />
      )}

      <FidelityAuthentication />
    </Box>
  );
};
